import React from "react";
import styled, { keyframes } from "styled-components";

// Floating animation for circles
const float = keyframes`
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 0.4;
  }
  50% {
    transform: translateY(-30px) rotate(180deg);
    opacity: 0.8;
  }
  100% {
    transform: translateY(0) rotate(360deg);
    opacity: 0.4;
  }
`;

// Wave-like movement for musical notes
const musicFloat = keyframes`
  0% {
    transform: translateY(0) translateX(0) rotate(0deg);
    opacity: 0.3;
  }
  25% {
    transform: translateY(-15px) translateX(10px) rotate(90deg);
    opacity: 0.7;
  }
  50% {
    transform: translateY(-25px) translateX(0) rotate(180deg);
    opacity: 1;
  }
  75% {
    transform: translateY(-15px) translateX(-10px) rotate(270deg);
    opacity: 0.7;
  }
  100% {
    transform: translateY(0) translateX(0) rotate(360deg);
    opacity: 0.3;
  }
`;

const ParticlesWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
`;

// Circle particles
const CircleParticle = styled.div`
  position: absolute;
  border-radius: 50%;
  background: ${({ $variant }) =>
    $variant === "primary"
      ? "#ff6b35"
      : $variant === "secondary"
      ? "#ff8c42"
      : $variant === "accent"
      ? "#ff4520"
      : "#ff6b35"};
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  animation: ${float} ${({ $duration }) => $duration}s infinite ease-in-out;
  animation-delay: ${({ $delay }) => $delay}s;
  ${({ $top, $left }) => `
    top: ${$top}%;
    left: ${$left}%;
  `}
`;

// Musical note particles
const MusicParticle = styled.div`
  position: absolute;
  color: ${({ $variant }) =>
    $variant === "primary"
      ? "#ff6b35"
      : $variant === "secondary"
      ? "#ff8c42"
      : $variant === "accent"
      ? "#ff4520"
      : "#ff6b35"};
  font-size: ${({ $size }) => $size}px;
  font-weight: bold;
  animation: ${musicFloat} ${({ $duration }) => $duration}s infinite ease-in-out;
  animation-delay: ${({ $delay }) => $delay}s;
  ${({ $top, $left }) => `
    top: ${$top}%;
    left: ${$left}%;
  `}
  user-select: none;
  text-shadow: 0 0 10px rgba(255, 107, 53, 0.3);
`;

const Particles = () => {
  // Circle particles - various sizes and $variants
  const circles = [
    { top: 15, left: 8, size: 3, variant: "primary", duration: 8, delay: 0 },
    {
      top: 75,
      left: 15,
      size: 5,
      variant: "secondary",
      duration: 10,
      delay: 1,
    },
    { top: 35, left: 85, size: 4, variant: "accent", duration: 7, delay: 2 },
    { top: 65, left: 92, size: 2, variant: "primary", duration: 9, delay: 3 },
    {
      top: 12,
      left: 75,
      size: 6,
      variant: "secondary",
      duration: 11,
      delay: 4,
    },
    { top: 88, left: 60, size: 3, variant: "accent", duration: 6, delay: 5 },
    { top: 45, left: 5, size: 4, variant: "primary", duration: 8, delay: 2.5 },
    { top: 25, left: 45, size: 2, variant: "secondary", duration: 9, delay: 6 },
    { top: 80, left: 82, size: 5, variant: "accent", duration: 7, delay: 1.5 },
    {
      top: 55,
      left: 25,
      size: 3,
      variant: "primary",
      duration: 10,
      delay: 4.5,
    },
  ];

  // Musical note particles - different note symbols
  const musicNotes = [
    {
      top: 20,
      left: 30,
      symbol: "♪",
      size: 16,
      variant: "primary",
      duration: 12,
      delay: 1,
    },
    {
      top: 70,
      left: 40,
      symbol: "♫",
      size: 18,
      variant: "secondary",
      duration: 14,
      delay: 3,
    },
    {
      top: 40,
      left: 65,
      symbol: "♬",
      size: 15,
      variant: "accent",
      duration: 11,
      delay: 5,
    },
    {
      top: 85,
      left: 35,
      symbol: "♪",
      size: 14,
      variant: "primary",
      duration: 13,
      delay: 2,
    },
    {
      top: 10,
      left: 55,
      symbol: "♫",
      size: 17,
      variant: "secondary",
      duration: 10,
      delay: 4,
    },
    {
      top: 60,
      left: 10,
      symbol: "♬",
      size: 16,
      variant: "accent",
      duration: 12,
      delay: 6,
    },
    {
      top: 30,
      left: 90,
      symbol: "♪",
      size: 15,
      variant: "primary",
      duration: 9,
      delay: 0.5,
    },
    {
      top: 90,
      left: 70,
      symbol: "♫",
      size: 13,
      variant: "secondary",
      duration: 11,
      delay: 3.5,
    },
  ];

  return (
    <ParticlesWrapper>
      {/* Render circle particles */}
      {circles.map((circle, index) => (
        <CircleParticle
          key={`circle-${index}`}
          $top={circle.top}
          $left={circle.left}
          $size={circle.size}
          $variant={circle.variant}
          $duration={circle.duration}
          $delay={circle.delay}
        />
      ))}

      {/* Render musical note particles */}
      {musicNotes.map((note, index) => (
        <MusicParticle
          key={`note-${index}`}
          $top={note.top}
          $left={note.left}
          $size={note.size}
          $variant={note.variant}
          $duration={note.duration}
          $delay={note.delay}
        >
          {note.symbol}
        </MusicParticle>
      ))}
    </ParticlesWrapper>
  );
};

export default Particles;
